<template>
  <div class="sa-overlay" @click.self="$emit('close')">
    <div class="sa-card">
      <!-- TOP HEADER -->
      <div class="sa-header">
        <div class="sa-header-left"><i class="fa fa-bar-chart"></i> Slide Analytics</div>
        <div class="sa-header-right">
          <div class="sa-session">SESSION ID: {{ session.sid }}</div>
          <i class="fa fa-share-alt" @click="handleShare"></i>
          <i class="fa fa-download" @click="handleDownload"></i>
          <i class="fa fa-times" @click="$emit('close')" style="margin-left: 16px"></i>
        </div>
      </div>
      <div class="sa-body">
        <!-- TITLE SECTION -->
        <div class="sa-title-row">
          <div class="sa-title-left">
            <div class="sa-breadcrumbs">
              Post-Game Reports > <span class="cyan">{{ slide ? slide.title.split(':')[0] : '' }}</span>
            </div>
            <h2>{{ slide ? slide.title : '' }}</h2>
          </div>
        </div>

        <!-- TWO COLS -->
        <div class="sa-grid">
          <!-- LEFT COL -->
          <div class="sa-col-left">
            <div class="sa-box">
              <!-- Desktop header (unchanged) -->
              <div class="sa-box-header sa-desktop-only" style="position: relative">
                <span class="cyan" style="font-size: 9px; font-weight: bold; letter-spacing: 1px">QUESTION PREVIEW</span>
                <div class="sa-preview-wrapper" @mouseenter="showPreview = true" @mouseleave="showPreview = false">
                  <i class="fa fa-question-circle" style="color: #1a3c40; font-size: 24px; cursor: help"></i>
                  <div v-if="showPreview && slide && slide.pic" class="sa-hover-preview" :style="`background-image: url(${Fn.hashToUrl(slide.pic)})`"></div>
                </div>
              </div>
              <!-- Mobile collapsible header -->
              <div class="sa-box-header sa-mobile-only sa-collapse-header" @click="mobileQuestionOpen = !mobileQuestionOpen">
                <!-- Left: label + ? icon in the same row -->
                <div style="display: flex; align-items: center; gap: 8px">
                  <span class="cyan" style="font-size: 9px; font-weight: bold; letter-spacing: 1px">QUESTION PREVIEW</span>
                  <div class="sa-preview-wrapper" @click.stop="mobilePreviewOpen = !mobilePreviewOpen">
                    <i class="fa fa-question-circle" style="color: #00ffff; font-size: 18px; cursor: pointer"></i>
                    <div
                      v-if="mobilePreviewOpen && slide && slide.pic"
                      class="sa-hover-preview sa-mobile-preview"
                      :style="`background-image: url(${Fn.hashToUrl(slide.pic)})`"></div>
                  </div>
                </div>
                <!-- Right: chevron -->
                <i class="fa" :class="mobileQuestionOpen ? 'fa-chevron-up' : 'fa-chevron-down'" style="color: #00ffff; font-size: 12px"></i>
              </div>
              <div :class="['sa-collapsible-content', {'sa-mobile-collapsed': !mobileQuestionOpen}]">
                <div class="sa-question-text" v-html="slide?.abstract?.data || ''"></div>
                <template v-if="Object.keys(parsedOptions).length > 1">
                  <div v-for="(qData, qId) in parsedOptions" :key="qId" class="sa-question-block">
                    <div class="sa-question-id">
                      {{ qData.type === 'choice' ? 'CHOICE QUESTION' : qData.type === 'text' ? 'TEXT QUESTION' : 'MULTIPLE CHOICE QUESTION' }}
                    </div>

                    <div class="sa-options-grid">
                      <div class="sa-option" v-for="(opt, idx) in qData.options" :key="idx">
                        <div class="sa-opt-char cyan">{{ String.fromCharCode(65 + idx) }}</div>
                        <div class="sa-opt-val" v-html="opt.data"></div>
                      </div>

                      <div v-if="!qData.options.length" class="sa-option" style="grid-column: span 2">
                        <div class="sa-opt-val" style="color: #8c99a0">No options configured.</div>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="sa-options-grid">
                    <div class="sa-option" v-for="(opt, idx) in Object.values(parsedOptions)[0]?.options || []" :key="idx">
                      <div class="sa-opt-char cyan">
                        {{ String.fromCharCode(65 + idx) }}
                      </div>
                      <div class="sa-opt-val" v-html="opt.data"></div>
                    </div>

                    <div v-if="!(Object.values(parsedOptions)[0]?.options || []).length" class="sa-option" style="grid-column: span 2">
                      <div class="sa-opt-val" style="color: #8c99a0">No options configured.</div>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <div class="sa-box">
              <!-- Desktop header (unchanged) -->
              <div class="sa-box-header sa-desktop-only" style="justify-content: space-between">
                <div>
                  <div style="font-size: 14px; font-weight: bold; color: #fff">Answer Distribution</div>
                  <div style="font-size: 10px; color: #8c99a0; margin-top: 4px">Breakdown of student selections</div>
                </div>
              </div>
              <!-- Mobile collapsible header -->
              <div class="sa-box-header sa-mobile-only sa-collapse-header" @click="mobileDistributionOpen = !mobileDistributionOpen">
                <div>
                  <div style="font-size: 14px; font-weight: bold; color: #fff">Answer Distribution</div>
                  <div style="font-size: 10px; color: #8c99a0; margin-top: 4px">Breakdown of student selections</div>
                </div>
                <i class="fa" :class="mobileDistributionOpen ? 'fa-chevron-up' : 'fa-chevron-down'" style="color: #00ffff; font-size: 12px"></i>
              </div>
              <!-- Content: always visible on desktop, toggled on mobile -->
              <div :class="['sa-collapsible-content', {'sa-mobile-collapsed': !mobileDistributionOpen}]">
                <div class="sa-bars">
                  <div v-for="(stats, qId) in answerStats" :key="qId" class="sa-question-block">
                    <div v-if="Object.keys(answerStats).length > 1" class="sa-question-id">
                      {{
                        parsedOptions[qId]?.type === 'choice'
                          ? 'CHOICE QUESTION'
                          : parsedOptions[qId]?.type === 'text'
                          ? 'TEXT QUESTION'
                          : 'MULTIPLE CHOICE QUESTION'
                      }}
                    </div>
                    <div class="sa-bars">
                      <div v-for="stat in stats" :key="stat.val" class="sa-bar-row">
                        <div class="sa-bar-label" :class="{cyan: stat.on}" :style="{color: stat.on ? '#00ffff' : '#8c99a0'}">{{ stat.val }}</div>
                        <div class="sa-bar-val" :class="{cyan: stat.on}" :style="{color: stat.on ? '#00ffff' : '#8c99a0'}">
                          {{ stat.ansPercent }}% <span v-if="stat.on">(Correct)</span>
                        </div>
                        <div class="sa-bar-bg">
                          <div class="sa-bar-fill" :style="{width: stat.ansPercent + '%', background: stat.on ? '#00ffff' : '#436a6e'}"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="Object.keys(answerStats).length === 0" style="color: #8c99a0; font-size: 12px; margin-top: 20px">No data available for distribution.</div>
                </div>
              </div>
            </div>
          </div>

          <!-- RIGHT COL -->
          <div class="sa-col-right">
            <div class="sa-box sa-flex-box">
              <div>
                <div style="font-size: 9px; color: #8c99a0; font-weight: bold; letter-spacing: 1px">GLOBAL ACCURACY</div>
                <div style="font-size: 32px; font-weight: bold; color: #00ffff; margin-top: 4px">{{ globalAccuracy }}%</div>
              </div>
              <div style="text-align: right">
                <div :style="{color: difficultyColor}" style="font-size: 9px; font-weight: bold; letter-spacing: 1px; margin-bottom: 4px">
                  DIFFICULTY: {{ difficultyText }}
                </div>
                <div class="sa-avg-metric">
                  Total Answers:
                  {{
                    currentAnalysisSlides.length && typeof currentAnalysisSlides[0]?.totalResponses === 'number'
                      ? currentAnalysisSlides[0].totalResponses
                      : responses.length
                  }}
                </div>
              </div>
            </div>

            <div class="sa-box sa-team-box">
              <div style="font-size: 9px; color: #8c99a0; font-weight: bold; letter-spacing: 1px; margin-bottom: 16px">TEAM PERFORMANCE ACCURACY</div>

              <div class="sa-team-list">
                <div v-for="(teams, qId) in teamPerformance" :key="qId" class="sa-question-block">
                  <div v-if="Object.keys(teamPerformance).length > 1" class="sa-question-id">
                    {{
                      parsedOptions[qId]?.type === 'choice'
                        ? 'CHOICE QUESTION'
                        : parsedOptions[qId]?.type === 'text'
                        ? 'TEXT QUESTION'
                        : 'MULTIPLE CHOICE QUESTION'
                    }}
                  </div>

                  <div class="sa-team-list">
                    <div v-for="(t, idx) in teams" :key="t.teamId" class="sa-team-bar-row">
                      <div class="sa-tbr-left">
                        {{ t.teamName }}
                      </div>

                      <div class="sa-tbr-right">{{ t.percent }}%</div>

                      <div class="sa-bar-bg">
                        <div class="sa-bar-fill" :class="t.percent === teams[0].percent ? 'cyan-bg' : 'grey-bg'" :style="{width: t.percent + '%'}"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="Object.keys(teamPerformance).length === 0" style="font-size: 10px; color: #8c99a0">No teams active</div>
              </div>
            </div>

            <!-- Desktop replay button (inside right col) -->
            <button class="sa-replay-btn q-mt-md sa-desktop-only" @click="handleDemoReplay"><i class="fa fa-play q-mr-sm"></i> SHOW DEMO REPLAY</button>
          </div>
        </div>
      </div>

      <!-- Mobile-only fixed footer replay button -->
      <div class="sa-mobile-footer sa-mobile-only">
        <button class="sa-replay-btn" @click="handleDemoReplay"><i class="fa fa-play q-mr-sm"></i> SHOW DEMO REPLAY</button>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex'

export default {
  name: 'SlideAnalyticsModal',
  props: {
    slide: {type: Object, default: () => null},
    responses: {type: Array, default: () => []},
    analysis: {type: Object, default: () => null},
  },
  data() {
    return {
      showPreview: false,
      mobileQuestionOpen: false,
      mobileDistributionOpen: false,
      mobilePreviewOpen: false,
    }
  },
  computed: {
    ...mapState(['session']),
    Fn() {
      return window.Fn || {}
    },
    currentAnalysisSlides() {
      if (!this.analysis || !Array.isArray(this.analysis.slides)) return []
      if (!this.slide) return []
      return this.analysis.slides.filter((as) => as.pageId === this.slide.pageId)
    },
    difficultyText() {
      if (!this.currentAnalysisSlides.length) return 'NO DATA'

      const levels = this.currentAnalysisSlides.map((s) => s.difficulty?.toLowerCase())

      if (levels.includes('difficult')) return 'DIFFICULT'
      if (levels.includes('medium')) return 'MEDIUM'
      if (levels.includes('easy')) return 'EASY'

      return 'NO DATA'
    },
    difficultyColor() {
      const d = this.difficultyText.toLowerCase()
      if (d === 'easy') return '#00ff88' // Green
      if (d === 'medium') return '#ffaa00' // Yellow/Orange
      if (d === 'difficult') return '#ff3366' // Red
      return '#8c99a0' // Default Gray for NO DATA
    },
    parsedOptions() {
      if (!this.slide || !Array.isArray(this.slide.questions)) return {}
      try {
        return this.slide.questions.reduce((acc, q) => {
          if (!Array.isArray(q.options) || q.type !== 'choice') {
            acc[q._id] = {type: q.type, options: []}
            return acc
          }

          acc[q._id] = {
            type: q.type,
            options: q.options.map((opt) => {
              const val = opt.data || opt.val || opt.text || opt
              return {
                data: val,
                on: opt.on === true || opt.isCorrect === true,
              }
            }),
          }
          return acc
        }, {})
      } catch (e) {
        return {}
      }
    },
    answerStats() {
      if (!Array.isArray(this.currentAnalysisSlides) || !this.currentAnalysisSlides.length) {
        return {}
      }

      const result = {}

      this.currentAnalysisSlides.forEach((slide) => {
        const qId = slide.questionId
        if (this.parsedOptions[qId]?.type === 'text') return

        if (!result[qId]) {
          result[qId] = []
        }

        ;(slide.optionsStats || []).forEach((opt) => {
          // Check if this option is already added for this question
          if (!result[qId].find((r) => r.val === opt.val)) {
            result[qId].push({
              val: opt.val,
              on: opt.on,
              ansPercent: opt.ansPercent,
            })
          }
        })
      })

      return result
    },
    globalAccuracy() {
      if (!this.currentAnalysisSlides.length) return 0

      const total = this.currentAnalysisSlides.reduce((sum, s) => sum + (s.totalResponses || 0), 0)
      const weightedAccuracy = this.currentAnalysisSlides.reduce((sum, s) => sum + (s.accuracy || 0) * (s.totalResponses || 0), 0)

      return total ? Math.round(weightedAccuracy / total) : 0
    },
    teamPerformance() {
      if (!this.currentAnalysisSlides.length) return {}

      const result = {}

      this.currentAnalysisSlides.forEach((slide) => {
        const qId = slide.questionId

        if (!result[qId]) {
          result[qId] = {}
        }

        ;(slide.teamStats || []).forEach((ts) => {
          if (!result[qId][ts.teamId]) {
            result[qId][ts.teamId] = {total: 0, correct: 0}
          }
          result[qId][ts.teamId].total += ts.total || 0
          result[qId][ts.teamId].correct += ts.correct || 0
        })
      })
      const formatted = {}

      Object.keys(result).forEach((qId) => {
        formatted[qId] = Object.keys(result[qId])
          .map((teamId) => {
            const t = result[qId][teamId]
            return {
              teamId,
              teamName: `Team ${String.fromCharCode(64 + Number(teamId))}`,
              percent: t.total ? Math.round((t.correct / t.total) * 100) : 0,
            }
          })
          .sort((a, b) => b.percent - a.percent)
      })

      return formatted
    },
  },
  mounted() {
    document.body.appendChild(this.$el)
  },
  destroyed() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }
  },
  methods: {
    handleShare() {},
    handleDownload() {},
    handleDemoReplay() {
      if (!this.slide) return

      // Look for the full question in the session's questions list
      const slideId = this.slide.page
      const fullQuestion = (this.session.questions || []).find((q) => q.page === slideId)

      const enrichedSlide = {
        ...this.slide,
        ...(fullQuestion || {}),
      }

      this.$emit('open-cpa-demo', enrichedSlide)
    },
  },
}
</script>

<style scoped>
.sa-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(12px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  font-family: Arial, sans-serif;
}

.sa-card {
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  background: #091315;
  border: none;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: none;
}

.sa-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: #0d2525;
  border-bottom: 1px solid #1a3c40;
  font-size: 12px;
  font-weight: bold;
  color: #fff;
}
.sa-header-left i {
  color: #00ffff;
  margin-right: 8px;
}
.sa-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  color: #8c99a0;
  font-size: 14px;
}
.sa-header-right i {
  cursor: pointer;
  transition: 0.2s;
}
.sa-header-right i:hover {
  color: #fff;
}
.sa-session {
  background: #1a3c40;
  color: #00ffff;
  font-size: 9px;
  padding: 4px 10px;
  border-radius: 12px;
  letter-spacing: 1px;
}

.sa-body {
  padding: 24px 32px;
  overflow-y: auto;
  flex: 1;
}

.sa-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 24px;
}
.sa-breadcrumbs {
  font-size: 10px;
  color: #8c99a0;
  font-weight: bold;
  margin-bottom: 8px;
}
.cyan {
  color: #00ffff;
}
.cyan-bg {
  background: #00ffff;
  color: #091315 !important;
}
.grey-bg {
  background: #1a3c40;
  color: #c9d1d6 !important;
}

.sa-title-left h2 {
  margin: 0;
  font-size: 24px;
  color: #fff;
}
.sa-analyse-btn {
  background: transparent;
  border: 1px solid #00ffff;
  color: #00ffff;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 1px;
  cursor: pointer;
  transition: 0.2s;
}
.sa-analyse-btn:hover {
  background: rgba(0, 255, 255, 0.1);
}

/* Hover Preview */
.sa-preview-wrapper {
  position: relative;
  overflow: visible;
}
.sa-box-header {
  overflow: visible;
}
.sa-box {
  overflow: visible;
}
.sa-hover-preview {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 10px;
  width: 320px;
  height: 180px;
  background: #0c181b;
  border: 1px solid #1a3c40;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.8);
  background-size: cover;
  background-position: center;
  z-index: 999999;
  pointer-events: none;
}

.sa-grid {
  display: flex;
  gap: 20px;
}
.sa-col-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.sa-col-right {
  flex: 1.1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.sa-box {
  background: #0c181b;
  border: 1px solid #1a3c40;
  border-radius: 8px;
  padding: 20px;
}
.sa-flex-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.sa-box-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

/* Left Col */
.sa-question-text {
  font-size: 16px;
  color: #fff;
  font-weight: bold;
  margin-bottom: 20px;
}
.sa-options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.sa-option {
  background: #121e24;
  border: 1px solid #1a3c40;
  border-radius: 6px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
}
.sa-opt-char {
  font-weight: bold;
  font-size: 12px;
}
.sa-opt-val {
  color: #fff;
  font-size: 14px;
  font-weight: bold;
}

.sa-bars {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.sa-bar-row {
  position: relative;
  padding-bottom: 12px;
}
.sa-bar-label {
  font-size: 10px;
  color: #fff;
  font-weight: bold;
  margin-bottom: 6px;
}
.sa-bar-val {
  position: absolute;
  right: 0;
  top: 0;
  font-size: 10px;
  font-weight: bold;
}
.sa-bar-bg {
  width: 100%;
  height: 6px;
  background: #121e24;
  border-radius: 3px;
  position: absolute;
  bottom: 0;
  overflow: hidden;
}
.sa-bar-fill {
  height: 100%;
  border-radius: 3px;
}

/* Right Col */
.sa-avg-metric {
  font-size: 10px;
  color: #8c99a0;
}
.sa-team-bar-row {
  position: relative;
  padding-bottom: 12px;
  margin-bottom: 16px;
}
.sa-team-bar-row:last-child {
  margin-bottom: 0;
}
.sa-tbr-left {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 10px;
  color: #8c99a0;
  font-weight: bold;
  margin-bottom: 8px;
}
.sa-tbr-avatar {
  width: 16px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 8px;
  border-radius: 2px;
  font-weight: bold;
}
.sa-tbr-right {
  position: absolute;
  right: 0;
  top: 2px;
  font-size: 10px;
  color: #fff;
  font-weight: bold;
}

.sa-adaptive-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
}
.sa-adapt-box {
  background: rgba(0, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 255, 0.2);
  padding: 16px 0;
  border-radius: 6px;
  text-align: center;
}
.sa-a-val {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 4px;
  color: #fff;
}
.sa-a-lbl {
  font-size: 8px;
  color: #8c99a0;
  font-weight: bold;
  letter-spacing: 1px;
}

.sa-adaptive-levels {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.sa-lvl-card {
  background: #121e24;
  border-radius: 6px;
  padding: 16px;
  text-align: center;
}

.sa-replay-btn {
  width: 100%;
  background: #00ffff;
  color: #091315;
  font-weight: bold;
  font-size: 12px;
  border: none;
  border-radius: 6px;
  padding: 16px;
  cursor: pointer;
  transition: 0.2s;
  box-shadow: 0 0 15px rgba(0, 255, 255, 0.3);
}
.sa-replay-btn:hover {
  background: #33ffff;
  box-shadow: 0 0 20px rgba(0, 255, 255, 0.5);
}

.sa-tip-box {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: transparent;
  border-left: 2px solid #00ffff;
  color: #8c99a0;
  font-size: 10px;
  line-height: 1.4;
  align-items: flex-start;
}
.sa-tip-box i {
  color: #00ffff;
  font-size: 14px;
  margin-top: 2px;
}

::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #1a3c40;
  border-radius: 3px;
}

/* ========== MOBILE ONLY ========== */
.sa-mobile-only {
  display: none;
}

@media (max-width: 768px) {
  .sa-desktop-only {
    display: none !important;
  }

  .sa-mobile-only {
    display: flex !important;
  }

  /* Single-column stacked layout */
  .sa-grid {
    flex-direction: column;
    gap: 12px;
  }

  .sa-col-left,
  .sa-col-right {
    flex: unset;
    width: 100%;
  }

  /* Scrollable body with bottom padding for fixed footer */
  .sa-body {
    padding: 16px;
    padding-bottom: 80px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* Collapsible section toggle header */
  .sa-collapse-header {
    cursor: pointer;
    justify-content: space-between;
    align-items: center;
    user-select: none;
  }

  /* Collapsed state hides body content on mobile */
  .sa-collapsible-content.sa-mobile-collapsed {
    display: none;
  }

  /* Scrollable team list on mobile */
  .sa-team-box {
    display: flex;
    flex-direction: column;
  }

  .sa-team-list {
    max-height: 180px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* Fixed footer for demo replay */
  .sa-mobile-footer {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background: #091315;
    border-top: 1px solid #1a3c40;
    padding: 12px 16px;
    z-index: 10;
    display: flex !important;
  }

  .sa-mobile-footer .sa-replay-btn {
    width: 100%;
    margin: 0;
  }

  /* Compact title on mobile */
  .sa-title-left h2 {
    font-size: 18px;
  }

  /* Slim session badge on mobile */
  .sa-session {
    display: none;
  }

  /* Mobile slide preview popup — positioned below-left of the ? icon */
  .sa-mobile-preview {
    position: fixed;
    right: 16px;
    top: auto;
    width: calc(100vw - 32px);
    max-width: 320px;
    height: 180px;
    margin-top: 8px;
    z-index: 999999;
  }

  /* Remove extra space when collapsible content is hidden */
  .sa-box:has(> .sa-collapsible-content.sa-mobile-collapsed) {
    padding-bottom: 20px;
  }
  .sa-box:has(> .sa-collapsible-content.sa-mobile-collapsed) .sa-box-header {
    margin-bottom: 0;
  }
}
.sa-question-id {
  margin: 20px 0 10px 0; /* adjust as needed */
}
</style>
